import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { RichTextEditor } from '../ui/rich-text-editor';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Info,
  Users,
  Plus,
  Edit,
  Trash2,
  Upload,
  Save,
  Image as ImageIcon,
  Mail,
  Phone,
  Linkedin,
  Facebook,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { normalizeMediaUrl, getAvatarUrl as resolveAvatarUrl } from '@/utils/media';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/components/auth/AuthContext';
import { ConfirmDialog } from '../ui/confirm-dialog';

// Start with empty About info; admins populate via backend only

export function AboutManagement() {
  const [aboutInfo, setAboutInfo] = useState({
    mission: '',
    vision: '',
    story: '',
    teamSectionTitle: 'Our Leadership Team',
    values: [] as any[],
    statistics: {
      totalJobs: '',
      happyClients: '',
      successfulPlacements: '',
      companiesServed: ''
    }
  } as any);

  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [newMember, setNewMember] = useState({
    name: '',
    position: '',
    bio: '',
    education: '',
    experience: '',
    linkedin: '',
    email: '',
    phone: '',
    image: '',
    facebook: '',
    whatsapp: ''
  });
  const [newMemberErrors, setNewMemberErrors] = useState({ name: false, position: false, image: false });
  const [editMemberErrors, setEditMemberErrors] = useState({ name: false, position: false, image: false });
  const [uploadingNewImage, setUploadingNewImage] = useState(false);
  const [uploadingEditImage, setUploadingEditImage] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState<any>(null);
  const isRichTextEmpty = (v: string) => {
    const str = String(v || '');
    const noTags = str
      .replace(/<br\s*\/?>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\u00a0/gi, ' ');
    return noTags.trim().length === 0;
  };

  // One-time purge to prevent localStorage-based restoration of deleted members
  useEffect(() => {
    try {
      localStorage.removeItem('mysql_megajob_db_team_members');
    } catch { }
  }, []);
  // Deduplicate team members by stable identifiers (email, LinkedIn, name+position)
  const dedupeMembers = (members: any[]) => {
    const seen = new Set<string>();
    return (Array.isArray(members) ? members : []).filter((m: any) => {
      const key = (
        (m?.email ? m.email.trim().toLowerCase() : '') ||
        (m?.linkedin ? m.linkedin.trim().toLowerCase() : '') ||
        `${(m?.name || '').trim().toLowerCase()}|${(m?.position || '').trim().toLowerCase()}`
      );
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const normalizeUrl = (url?: string) => {
    const u = (url || '').trim();
    if (!u) return '';
    return u.startsWith('http') ? u : `https://${u}`;
  };
  const ensureUniqueIds = (members: any[]) => {
    const keyOf = (m: any) => (
      m?.email?.trim().toLowerCase() ||
      m?.linkedin?.trim().toLowerCase() ||
      `${(m?.name || '').trim().toLowerCase()}|${(m?.position || '').trim().toLowerCase()}`
    );
    return (Array.isArray(members) ? members : []).map((m: any) => {
      const serverId = String((m?.id ?? m?._id ?? '')).trim();
      return {
        ...m,
        id: serverId || keyOf(m)
      };
    });
  };
  const applyServerTeamMembers = (members: any[]) => ensureUniqueIds(dedupeMembers(members));
  const { user, isAuthenticated } = useAuth();
  const isAdminOrSuperAdmin = () => {
    const role = String(user?.user_type || '').toLowerCase();
    return !!isAuthenticated && (role === 'admin' || role === 'super_admin');
  };
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [infoRes, teamRes] = await Promise.all([
          apiClient.getAboutInfo().catch(() => null),
          apiClient.getTeamMembers().catch(() => null)
        ]);
        const infoRaw = infoRes && (infoRes as any)?.about ? (infoRes as any).about : infoRes;
        const statsRaw = ((infoRaw as any)?.statistics) || {};
        const normalizedAbout = {
          mission: String(((infoRaw as any)?.mission) || ''),
          vision: String(((infoRaw as any)?.vision) || ''),
          story: String(((infoRaw as any)?.story) || ''),
          teamSectionTitle: String(((infoRaw as any)?.teamSectionTitle) || 'Our Leadership Team'),
          values: Array.isArray((infoRaw as any)?.values) ? (infoRaw as any).values : [],
          statistics: {
            totalJobs: String(statsRaw?.totalJobs || ''),
            happyClients: String(statsRaw?.happyClients || ''),
            successfulPlacements: String(statsRaw?.successfulPlacements || ''),
            companiesServed: String(statsRaw?.companiesServed || '')
          }
        } as any;

        const rawMembers = Array.isArray((teamRes as any)?.members)
          ? (teamRes as any).members
          : (Array.isArray(teamRes) ? (teamRes as any) : []);
        const nextMembers = applyServerTeamMembers(rawMembers);

        if (!mounted) return;
        setAboutInfo(normalizedAbout);
        setTeamTitleSaved(normalizedAbout.teamSectionTitle || 'Our Leadership Team');
        setTeamMembers(nextMembers);
      } catch {
        if (!mounted) return;
        setAboutInfo({
          mission: '',
          vision: '',
          story: '',
          teamSectionTitle: 'Our Leadership Team',
          values: [],
          statistics: {
            totalJobs: '',
            happyClients: '',
            successfulPlacements: '',
            companiesServed: ''
          }
        } as any);
        setTeamTitleSaved('Our Leadership Team');
        setTeamMembers([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const [teamTitleSaved, setTeamTitleSaved] = useState<string>('Our Leadership Team');
  const persistAbout = async (nextInfo: any, suppressToast = false) => {
    const stats = nextInfo?.statistics || {};
    const normalized = {
      mission: String(nextInfo?.mission || ''),
      vision: String(nextInfo?.vision || ''),
      story: String(nextInfo?.story || ''),
      teamSectionTitle: String(nextInfo?.teamSectionTitle || 'Our Leadership Team'),
      values: Array.isArray(nextInfo?.values) ? nextInfo.values : [],
      statistics: {
        totalJobs: String(stats?.totalJobs || ''),
        happyClients: String(stats?.happyClients || ''),
        successfulPlacements: String(stats?.successfulPlacements || ''),
        companiesServed: String(stats?.companiesServed || '')
      }
    } as any;
    setAboutInfo(normalized);
    try {
      await apiClient.updateAboutInfo(normalized);
      setTeamTitleSaved(normalized.teamSectionTitle || 'Our Leadership Team');
      if (!suppressToast) toast.success('About info saved');
    } catch (err) {
      if (!suppressToast) toast.error('Failed to save About info');
      throw err;
    }
  };

  const persistMembers = async (nextMembers: any[], opts: { successMessage?: string, suppressToast?: boolean } = {}) => {
    const normalized = applyServerTeamMembers(nextMembers);
    try {
      const res = await apiClient.saveTeamMembers(normalized);
      const saved = Array.isArray((res as any)?.members) ? (res as any).members : normalized;
      setTeamMembers(applyServerTeamMembers(saved));
      // Refresh from backend to ensure UI reflects true DB state
      try {
        const teamRes = await apiClient.getTeamMembers();
        const raw = Array.isArray((teamRes as any)?.members)
          ? (teamRes as any).members
          : (Array.isArray(teamRes) ? (teamRes as any) : []);
        setTeamMembers(applyServerTeamMembers(raw));
      } catch { /* noop */ }
      if (!opts.suppressToast) toast.success(opts.successMessage || 'Team members saved');
    } catch (error: any) {
      console.error('Failed to save team members:', error);
      const status = (error?.status ?? error?.code);
      if (!opts.suppressToast) {
        if (status === 401 || status === 403) {
          toast.error('Not authorized. Super admin role required to save.');
        } else {
          toast.error('Backend unavailable. Please connect DB and try again.');
        }
      }
      // Refresh current state from backend so UI matches actual DB
      try {
        const teamRes = await apiClient.getTeamMembers();
        const raw = Array.isArray((teamRes as any)?.members)
          ? (teamRes as any).members
          : (Array.isArray(teamRes) ? teamRes as any : []);
        setTeamMembers(applyServerTeamMembers(raw));
      } catch { /* noop */ }
      if (opts.suppressToast) throw error;
    }
  };

  const clearAllMembers = async () => {
    await persistMembers([]);
    // No local fallbacks: team must be empty until backend save
    // Success toast handled inside persistMembers
  };

  const keepOnlyOneMember = async () => {
    if (!teamMembers.length) {
      toast.info('No team members to keep.');
      return;
    }
    await persistMembers([teamMembers[0]]);
    toast.success('Kept one team member and removed the rest.');
  };

  const removeDuplicateMembers = async () => {
    if (!teamMembers.length) {
      toast.info('No team members to deduplicate.');
      return;
    }
    const before = teamMembers.length;
    const deduped = dedupeMembers(teamMembers);
    await persistMembers(deduped);
    const removed = before - deduped.length;
    toast.success(removed > 0 ? `Removed ${removed} duplicate(s).` : 'No duplicates found.');
  };

  const handleSaveAbout = async () => {
    try {
      await persistAbout(aboutInfo, true);
      await persistMembers(teamMembers, { suppressToast: true });
      toast.success('About information updated successfully!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update about information');
    }
  };

  const handleAddMember = async () => {
    if (!isAdminOrSuperAdmin()) {
      toast.error('Only Admin or Super Admin can add team members');
      return;
    }
    const nameEmpty = !(newMember.name || '').trim();
    const positionEmpty = !(newMember.position || '').trim();
    const imageEmpty = !String(newMember.image || '').trim();
    const nextErr = { name: nameEmpty, position: positionEmpty, image: imageEmpty } as any;
    setNewMemberErrors(nextErr);
    if (nameEmpty || positionEmpty || imageEmpty) {
      toast.error('Name, Position and Image are required');
      return;
    }

    const member = {
      ...newMember,
      image: newMember.image || ''
    };

    try {
      const resp = await apiClient.upsertTeamMember(member);
      const list = Array.isArray(resp?.members) ? resp.members : [];
      setTeamMembers(applyServerTeamMembers(list));
      toast.success('Team member added successfully!');
    } catch (e: any) {
      const status = e?.status ?? e?.code;
      const msg = e?.message || 'Failed to add team member';
      if (status === 503) {
        toast.error('Database not connected. Please connect DB first.');
      } else if (status === 403) {
        toast.error('This member was deleted and is blocked from re-adding');
      } else if (status === 409) {
        toast.error('Duplicate member detected. Please update existing entry');
      } else {
        toast.error(msg);
      }
      return;
    }
    setNewMember({
      name: '',
      position: '',
      bio: '',
      education: '',
      experience: '',
      linkedin: '',
      email: '',
      phone: '',
      image: '',
      facebook: '',
      whatsapp: ''
    });
    setNewMemberErrors({ name: false, position: false, image: false });
    setIsAddMemberOpen(false);
    // Success toast handled inside persistMembers
  };

  const handleMemberImageUpload = async (file: File, target: 'new' | 'edit') => {
    if (!file) return;
    try {
      if (target === 'new') setUploadingNewImage(true); else setUploadingEditImage(true);
      const makeKey = (m: any) => {
        const email = (m?.email || '').trim().toLowerCase();
        const linkedin = (m?.linkedin || '').trim().toLowerCase();
        const name = (m?.name || '').trim().toLowerCase();
        const position = (m?.position || '').trim().toLowerCase();
        return email || linkedin || `${name}|${position}` || String(Date.now());
      };
      const memberId = target === 'edit'
        ? (editingMember?._id || editingMember?.id || makeKey(editingMember))
        : makeKey(newMember);
      const res = await apiClient.uploadTeamMemberImage(file, memberId);
      const url = res?.url || res?.secure_url || res?.path;
      if (url) {
        if (target === 'new') {
          const normalized = normalizeMediaUrl(url) || url;
          setNewMember(prev => ({ ...prev, image: normalized }));
          setNewMemberErrors(prev => ({ ...prev, image: false }));
        } else if (editingMember) {
          const normalized = normalizeMediaUrl(url) || url;
          setEditingMember({ ...editingMember, image: normalized });
          setEditMemberErrors(prev => ({ ...prev, image: false }));
        }
        toast.success('Image uploaded and URL set');
      } else {
        toast.warning('Upload succeeded, but no URL returned');
      }
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      if (target === 'new') setUploadingNewImage(false); else setUploadingEditImage(false);
    }
  };

  const handleDeleteMember = async (id: string) => {
    try {
      // Resolve backend ID if a computed key was passed
      let delId = String(id || '').trim();
      if (!delId || delId.length < 8) {
        try {
          const curr = await apiClient.getTeamMembers();
          const list = Array.isArray((curr as any)?.members) ? (curr as any).members : [];
          const match = (list || []).find((m: any) => {
            const key = (
              (m?.email ? m.email.trim().toLowerCase() : '') ||
              (m?.linkedin ? m.linkedin.trim().toLowerCase() : '') ||
              `${(m?.name || '').trim().toLowerCase()}|${(m?.position || '').trim().toLowerCase()}`
            );
            const mid = String((m?._id ?? m?.id ?? '')).trim();
            return key === id || mid === id;
          });
          if (match) delId = String((match?._id ?? match?.id ?? id) || '').trim();
        } catch { }
      }

      const res = await apiClient.deleteTeamMember(delId);
      const raw = Array.isArray((res as any)?.members) ? (res as any).members : (Array.isArray(res) ? res as any : []);
      let next = applyServerTeamMembers(raw);
      // Defensive: ensure the deleted key is not present even if backend returned stale list
      next = next.filter((m: any) => {
        const key = (
          (m?.email ? m.email.trim().toLowerCase() : '') ||
          (m?.linkedin ? m.linkedin.trim().toLowerCase() : '') ||
          `${(m?.name || '').trim().toLowerCase()}|${(m?.position || '').trim().toLowerCase()}`
        );
        return key !== id && m.id !== id && m._id !== id && m.id !== delId && m._id !== delId;
      });
      setTeamMembers(next);
      toast.success('Team member removed');
      // Refresh from server to ensure blocked entries do not return
      try {
        const latest = await apiClient.getTeamMembers();
        const list = Array.isArray((latest as any)?.members) ? (latest as any).members : [];
        const final = applyServerTeamMembers(list);
        // Defensive once more
        setTeamMembers(final.filter((m: any) => {
          const key = (
            (m?.email ? m.email.trim().toLowerCase() : '') ||
            (m?.linkedin ? m.linkedin.trim().toLowerCase() : '') ||
            `${(m?.name || '').trim().toLowerCase()}|${(m?.position || '').trim().toLowerCase()}`
          );
          return key !== id && m.id !== id && m._id !== id && m.id !== delId && m._id !== delId;
        }));
      } catch { }
    } catch (err: any) {
      try {
        const normalized = applyServerTeamMembers(teamMembers);
        const filtered = normalized.filter(m => {
          const key = (
            (m?.email ? m.email.trim().toLowerCase() : '') ||
            (m?.linkedin ? m.linkedin.trim().toLowerCase() : '') ||
            `${(m?.name || '').trim().toLowerCase()}|${(m?.position || '').trim().toLowerCase()}`
          );
          return key !== id && m.id !== id && m._id !== id;
        });
        await persistMembers(filtered);
        toast.success('Team member removed');
      } catch (e: any) {
        toast.error(e?.message || err?.message || 'Failed to delete team member');
      }
    }
  };

  const addValue = () => {
    persistAbout({
      ...aboutInfo,
      values: [...(aboutInfo.values || []), { title: '', description: '' }]
    });
  };
  const removeValue = (index: number) => {
    const next = [...(aboutInfo.values || [])];
    next.splice(index, 1);
    setAboutInfo({ ...aboutInfo, values: next });
  };
  const saveValues = async () => {
    await persistAbout(aboutInfo);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">About Section Management</h2>
          <p className="text-gray-600">Manage about page content and team information</p>
        </div>
        <Button onClick={handleSaveAbout} className="flex items-center space-x-2">
          <Save className="h-4 w-4" />
          <span>Save All Changes</span>
        </Button>
      </div>

      <Tabs defaultValue="about" className="space-y-6">
        <TabsList>
          <TabsTrigger value="about">About Information</TabsTrigger>
          <TabsTrigger value="team">Management Team</TabsTrigger>
        </TabsList>

        {/* About Information */}
        <TabsContent value="about" className="space-y-6">
          {/* Mission & Vision */}
          <Card>
            <CardHeader>
              <CardTitle>Mission & Vision</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mission">Mission Statement</Label>
                <Textarea
                  id="mission"
                  value={aboutInfo.mission}
                  onChange={(e) => setAboutInfo({ ...aboutInfo, mission: e.target.value })}
                  placeholder="Enter mission statement"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vision">Vision Statement</Label>
                <Textarea
                  id="vision"
                  value={aboutInfo.vision}
                  onChange={(e) => setAboutInfo({ ...aboutInfo, vision: e.target.value })}
                  placeholder="Enter vision statement"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Company Story */}
          <Card>
            <CardHeader>
              <CardTitle>Company Story</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                id="story"
                value={aboutInfo.story}
                onChange={(e) => setAboutInfo({ ...aboutInfo, story: e.target.value })}
                placeholder="Write company story"
                rows={6}
              />
            </CardContent>
          </Card>

          {/* Values */}
          <Card>
            <CardHeader>
              <CardTitle>Core Values</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="hidden md:grid md:grid-cols-12 md:gap-3">
                  <div className="md:col-span-4">
                    <Label>Title</Label>
                  </div>
                  <div className="md:col-span-7">
                    <Label>Description</Label>
                  </div>
                  <div className="md:col-span-1" />
                </div>
                {(aboutInfo.values || []).map((value, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                    <div className="md:col-span-4">
                      <Input
                        placeholder="Title"
                        value={value.title}
                        onChange={(e) => {
                          const updated = [...(aboutInfo.values || [])];
                          updated[index] = { ...updated[index], title: e.target.value };
                          setAboutInfo({ ...aboutInfo, values: updated });
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="md:col-span-7">
                      <Textarea
                        placeholder="Description"
                        value={value.description}
                        onChange={(e) => {
                          const updated = [...(aboutInfo.values || [])];
                          updated[index] = { ...updated[index], description: e.target.value };
                          setAboutInfo({ ...aboutInfo, values: updated });
                        }}
                        rows={3}
                        className="text-sm"
                      />
                    </div>
                    <div className="md:col-span-1 flex md:justify-end">
                      <Button variant="outline" size="sm" onClick={() => removeValue(index)}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={addValue}><Plus className="h-4 w-4 mr-2" />Add Value</Button>
                  <Button size="sm" onClick={saveValues}><Save className="h-4 w-4 mr-2" />Save Values</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>Company Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="totalJobs">Total Jobs Posted</Label>
                  <Input
                    id="totalJobs"
                    value={aboutInfo.statistics.totalJobs}
                    onChange={(e) => setAboutInfo({
                      ...aboutInfo,
                      statistics: { ...aboutInfo.statistics, totalJobs: e.target.value }
                    })}
                    placeholder="e.g., 10,000+"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="happyClients">Happy Clients</Label>
                  <Input
                    id="happyClients"
                    value={aboutInfo.statistics.happyClients}
                    onChange={(e) => setAboutInfo({
                      ...aboutInfo,
                      statistics: { ...aboutInfo.statistics, happyClients: e.target.value }
                    })}
                    placeholder="e.g., 5,000+"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="successfulPlacements">Successful Placements</Label>
                  <Input
                    id="successfulPlacements"
                    value={aboutInfo.statistics.successfulPlacements}
                    onChange={(e) => setAboutInfo({
                      ...aboutInfo,
                      statistics: { ...aboutInfo.statistics, successfulPlacements: e.target.value }
                    })}
                    placeholder="e.g., 15,000+"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companiesServed">Companies Served</Label>
                  <Input
                    id="companiesServed"
                    value={aboutInfo.statistics.companiesServed}
                    onChange={(e) => setAboutInfo({
                      ...aboutInfo,
                      statistics: { ...aboutInfo.statistics, companiesServed: e.target.value }
                    })}
                    placeholder="e.g., 2,500+"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Management Team */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="space-y-4">
                <div className="max-w-xl">
                  <Label htmlFor="teamSectionTitle">Team Section Title</Label>
                  <div className="flex gap-2">
                    <Input
                      id="teamSectionTitle"
                      value={aboutInfo.teamSectionTitle || ''}
                      onChange={(e) => setAboutInfo({ ...aboutInfo, teamSectionTitle: e.target.value })}
                      placeholder="e.g., Our Leadership Team"
                    />
                    <Button variant="outline" onClick={async () => { await persistAbout(aboutInfo); }}>
                      {String((aboutInfo.teamSectionTitle || '')).trim() !== String((teamTitleSaved || '')).trim() ? 'Update Title' : 'Save Title'}
                    </Button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <CardTitle>Management Team ({teamMembers.length})</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setConfirmAllOpen(true)}
                    >
                      Remove All
                    </Button>
                    <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Team Member
                        </Button>
                      </DialogTrigger>
                      <DialogContent dir="ltr" className="max-w-2xl max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Add Team Member</DialogTitle>
                          <DialogDescription>
                            Add a new member to the management team.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="memberName">Full Name *</Label>
                              <Input
                                id="memberName"
                                value={newMember.name}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setNewMember({ ...newMember, name: v });
                                  setNewMemberErrors(prev => ({ ...prev, name: false }));
                                }}
                                placeholder="Enter full name"
                                aria-invalid={newMemberErrors.name || undefined}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="memberPosition">Position *</Label>
                              <Input
                                id="memberPosition"
                                value={newMember.position}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setNewMember({ ...newMember, position: v });
                                  setNewMemberErrors(prev => ({ ...prev, position: false }));
                                }}
                                placeholder="Enter position/title"
                                aria-invalid={newMemberErrors.position || undefined}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="memberBio">Biography</Label>
                            <RichTextEditor
                              value={newMember.bio}
                              onChange={(value) => {
                                setNewMember({ ...newMember, bio: value });
                              }}
                              placeholder="Write team member bio here..."
                              showImageUpload={false}
                              showLinkInsert={true}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="memberEducation">Education</Label>
                              <Input
                                id="memberEducation"
                                value={newMember.education}
                                onChange={(e) => setNewMember({ ...newMember, education: e.target.value })}
                                placeholder="Educational background"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="memberExperience">Experience</Label>
                              <Input
                                id="memberExperience"
                                value={newMember.experience}
                                onChange={(e) => setNewMember({ ...newMember, experience: e.target.value })}
                                placeholder="Years of experience"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="memberEmail">Email</Label>
                              <Input
                                id="memberEmail"
                                type="email"
                                value={newMember.email}
                                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                                placeholder="Email address"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="memberLinkedin">LinkedIn</Label>
                              <Input
                                id="memberLinkedin"
                                value={newMember.linkedin}
                                onChange={(e) => setNewMember({ ...newMember, linkedin: e.target.value })}
                                placeholder="LinkedIn profile URL"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="memberFacebook">Facebook</Label>
                              <Input
                                id="memberFacebook"
                                value={newMember.facebook}
                                onChange={(e) => setNewMember({ ...newMember, facebook: e.target.value })}
                                placeholder="Facebook profile/page URL"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="memberWhatsapp">WhatsApp</Label>
                              <Input
                                id="memberWhatsapp"
                                value={newMember.whatsapp}
                                onChange={(e) => setNewMember({ ...newMember, whatsapp: e.target.value })}
                                placeholder="WhatsApp number e.g. 9800000000"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="memberPhone">Phone</Label>
                              <Input
                                id="memberPhone"
                                value={newMember.phone}
                                onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                                placeholder="+977-9800000000"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Upload Profile Image *</Label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    setNewMemberErrors(prev => ({ ...prev, image: false }));
                                    handleMemberImageUpload(file, 'new');
                                  }
                                }}
                              />
                              {newMemberErrors.image && (
                                <p className="text-xs text-red-600 mt-1">Profile image is required</p>
                              )}
                              {uploadingNewImage && (
                                <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsAddMemberOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleAddMember}>Add Member</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {teamMembers.map(member => (
                  <Card key={member.id}>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="aspect-square w-full max-w-[120px] bg-gray-100  overflow-hidden">
                            <ImageWithFallback
                              src={normalizeMediaUrl(member.image) || member.image}
                              alt={member.name || 'Team Member'}
                              className="w-full h-full object-cover"
                              fallbackSrc={resolveAvatarUrl(member.name || 'Team Member', 120)}
                            />
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingMember(member)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setConfirmItem(member); setConfirmOpen(true); }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold break-words">{member.name}</h3>
                          <p className="text-sm text-blue-600 break-words">{member.position}</p>
                          {member.bio && (
                            <div className="text-sm text-gray-600 line-clamp-4" dangerouslySetInnerHTML={{ __html: member.bio }} />
                          )}
                          <div className="space-y-2 bg-blue-50 p-3 ">
                            {member.linkedin && (
                              <a
                                href={normalizeUrl(member.linkedin)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800 break-all"
                              >
                                <Linkedin className="w-4 h-4" /> {member.linkedin}
                              </a>
                            )}
                            {member.facebook && (
                              <a
                                href={normalizeUrl(member.facebook)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800 break-all"
                              >
                                <Facebook className="w-4 h-4" /> {member.facebook}
                              </a>
                            )}
                            {member.email && (
                              <a
                                href={`mailto:${member.email}`}
                                className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 break-all"
                              >
                                <Mail className="w-4 h-4" /> {member.email}
                              </a>
                            )}
                            {member.phone && (
                              <a
                                href={`tel:${(member.phone || '').replace(/\s+/g, '')}`}
                                className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
                              >
                                <Phone className="w-4 h-4" /> {member.phone}
                              </a>
                            )}
                            {member.whatsapp && (
                              <a
                                href={`https://wa.me/${String(member.whatsapp).replace(/\D+/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-green-700 hover:text-green-800"
                              >
                                <MessageCircle className="w-4 h-4" /> WhatsApp: {member.whatsapp}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete team member?"
        description="Are you sure you want to delete this team member? This action cannot be undone."
        type="danger"
        itemName={confirmItem?.name}
        confirmText="Delete"
        onConfirm={async () => {
          const delId = String((confirmItem?._id ?? confirmItem?.id ?? '')).trim();
          if (!delId) return;
          await handleDeleteMember(delId);
        }}
      />

      <ConfirmDialog
        open={confirmAllOpen}
        onOpenChange={setConfirmAllOpen}
        title="Remove all team members?"
        description="This will permanently delete all team members and their images."
        type="danger"
        confirmText="Delete All"
        onConfirm={async () => {
          try {
            const res = await apiClient.deleteAllTeamMembers();
            const list = Array.isArray((res as any)?.members) ? (res as any).members : [];
            setTeamMembers(list);
            try { localStorage.removeItem('mysql_megajob_db_team_members'); } catch { }
            toast.success('All team members removed');
          } catch (err: any) {
            const status = err?.status ?? err?.code;
            if (status === 401 || status === 403) {
              toast.error('Not authorized. Super admin role required to delete all.');
            } else {
              toast.error('Failed to delete all team members. Please check backend.');
            }
          }
        }}
      />

      {/* Edit Member Dialog */}
      <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
        <DialogContent dir="ltr" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>
              Update member details and profile information.
            </DialogDescription>
          </DialogHeader>
          {editingMember && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editMemberName">Full Name *</Label>
                  <Input
                    id="editMemberName"
                    value={editingMember.name}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditingMember({ ...editingMember, name: v });
                      setEditMemberErrors(prev => ({ ...prev, name: false }));
                    }}
                    aria-invalid={editMemberErrors.name || undefined}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editMemberPosition">Position *</Label>
                  <Input
                    id="editMemberPosition"
                    value={editingMember.position}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditingMember({ ...editingMember, position: v });
                      setEditMemberErrors(prev => ({ ...prev, position: false }));
                    }}
                    aria-invalid={editMemberErrors.position || undefined}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editMemberBio">Bio</Label>
                <RichTextEditor
                  value={editingMember.bio}
                  onChange={(value) => {
                    setEditingMember({ ...editingMember, bio: value });
                  }}
                  placeholder="Write team member bio here..."
                  showImageUpload={false}
                  showLinkInsert={true}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editMemberEducation">Education</Label>
                  <Input
                    id="editMemberEducation"
                    value={editingMember.education}
                    onChange={(e) => setEditingMember({ ...editingMember, education: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editMemberExperience">Experience</Label>
                  <Input
                    id="editMemberExperience"
                    value={editingMember.experience}
                    onChange={(e) => setEditingMember({ ...editingMember, experience: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editMemberEmail">Email</Label>
                  <Input
                    id="editMemberEmail"
                    type="email"
                    value={editingMember.email}
                    onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editMemberLinkedin">LinkedIn</Label>
                  <Input
                    id="editMemberLinkedin"
                    value={editingMember.linkedin}
                    onChange={(e) => setEditingMember({ ...editingMember, linkedin: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editMemberFacebook">Facebook</Label>
                  <Input
                    id="editMemberFacebook"
                    value={editingMember.facebook || ''}
                    onChange={(e) => setEditingMember({ ...editingMember, facebook: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editMemberWhatsapp">WhatsApp</Label>
                  <Input
                    id="editMemberWhatsapp"
                    value={editingMember.whatsapp || ''}
                    onChange={(e) => setEditingMember({ ...editingMember, whatsapp: e.target.value })}
                    placeholder="9800000000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editMemberPhone">Phone</Label>
                  <Input
                    id="editMemberPhone"
                    value={editingMember.phone || ''}
                    onChange={(e) => setEditingMember({ ...editingMember, phone: e.target.value })}
                    placeholder="+977-9800000000"
                  />
                </div>
                <div className="space-y-2">
                  {/* spacer */}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Upload Profile Image *</Label>
                {editingMember.image && (
                  <div className="aspect-square w-full max-w-[120px] bg-gray-100  overflow-hidden mb-2">
                    <img
                      src={normalizeMediaUrl(editingMember.image) || editingMember.image}
                      alt={editingMember.name || 'Team Member'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setEditMemberErrors(prev => ({ ...prev, image: false }));
                      handleMemberImageUpload(file, 'edit');
                    }
                  }}
                />
                {editMemberErrors.image && (
                  <p className="text-xs text-red-600 mt-1">Profile image is required</p>
                )}
                {uploadingEditImage && (
                  <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                )}
                <div className="mt-2 flex gap-2">
                  <Button variant="outline" onClick={() => setEditingMember({ ...editingMember, image: '' })}>Clear Image</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Bio font controls removed; use RichTextEditor toolbar */}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingMember(null)}>Cancel</Button>
                <Button onClick={async () => {
                  const nameEmpty = !(editingMember.name || '').trim();
                  const positionEmpty = !(editingMember.position || '').trim();
                  const imageEmpty = !String(editingMember.image || '').trim();
                  const nextErr = { name: nameEmpty, position: positionEmpty, image: imageEmpty } as any;
                  setEditMemberErrors(nextErr);
                  if (nameEmpty || positionEmpty || imageEmpty) {
                    toast.error('Name, Position and Image are required');
                    return;
                  }
                  try {
                    if (!isAdminOrSuperAdmin()) { toast.error('Only Admin or Super Admin can update team members'); return; }
                    const resp = await apiClient.upsertTeamMember(editingMember);
                    const list = Array.isArray(resp?.members) ? resp.members : teamMembers;
                    setTeamMembers(applyServerTeamMembers(list));
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to update team member');
                    return;
                  }
                  setEditingMember(null);
                  toast.success('Team member updated successfully!');
                }}>Save Changes</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

